<template lang="html">
  <div class="template">
    <p v-if="loading">
      Loading •••
    </p>
    <section
      v-else
      class="wrapper"
    >
      <preview :template-url="activeTemplate.url" />

      <div class="side-bar">
        <template-selector
          v-if="numberOfTemplatesAvailable > 1"
          :templates="enabledTemplates"
          :default-template-name="defaultTemplateName"
          :toolkit-ui-routing="toolkitUiRouting"
          @changeTemplate="changeTemplate"
        />

        <settingsForm
          :key="activeTemplate.url"
          :specific-origin-model="specificOriginModel"
          :specific-translations-model="specificTranslationsModel"
          :locales-allowed="localesAllowed"
          :template-name="selectedTemplateName"
          :template-url="activeTemplate.url"
          @validate="validate"
        />

        <results
          v-show="jobs.length > 0"
          :user-email="userEmail"
          :selected-template="selectedTemplateName"
          :jobs="jobs"
        />
      </div>
    </section>
  </div>
</template>

<script>
import TemplateSelector from '@/components/TemplateSelector'
import SettingsForm from '@/components/SettingsForm'
import Preview from '@/components/Preview'
import Results from '@/components/Results'
import messages from '@/i18n/messages'
import dateTimeFormats from '@/i18n/dateTimeFormats'
import numberFormats from '@/i18n/numberFormats'
import bus from '@/bus'

export default {
  name: 'TemplateDdv',

  components: { TemplateSelector, SettingsForm, Preview, Results },

  props: {
    locale: { type: String, default: 'fr' },
    service: { type: Object, required: true },
    userEmail: { type: String, default: '' },
    userName: { type: String, default: '' },
    templates: { type: Array, required: true },
    defaultTemplateName: { type: String, required: true },
    toolkitUiRouting: { type: Object, required: true }
  },

  data () {
    return {
      loading: true,
      specificOriginModel: [],
      specificTranslationsModel: [],
      localesAllowed: [],
      rendering: false,
      jobs: [],
      selectedTemplateName: this.defaultTemplateName,
      templatesConfig: {}
    }
  },

  computed: {
    activeTemplate () {
      const res = this.templates.find(el => el.name === this.selectedTemplateName)
      console.log(res, this.templates)
      return res
    },

    enabledTemplates () {
      return this.templates.filter(x => !x.disabled)
    },

    numberOfTemplatesAvailable () {
      let count = 0
      this.templates.forEach(template => {
        if (!template.disabled) count++
      })
      return count
    }
  },

  created () {
    console.log(this)
    this.mergeLocales()
    this.getTemplate()
    this.getJobs()
    this.addEventListeners()
  },

  mounted () {
    this.$i18n.locale = this.locale
  },

  beforeDestroy () {
    this.removeEventListeners()
  },

  methods: {
    changeTemplate (name) {
      this.selectedTemplateName = name
      this.getTemplate()
    },

    mergeLocales () {
      Object.keys(messages)
        .map(locale => {
          this.$i18n.mergeLocaleMessage(locale, messages[locale])
        })
      Object.keys(dateTimeFormats)
        .map(locale => {
          this.$i18n.mergeDateTimeFormat(locale, dateTimeFormats[locale])
        })
      Object.keys(numberFormats)
        .map(locale => {
          this.$i18n.mergeNumberFormat(locale, numberFormats[locale])
        })
    },

    async getTemplate () {
      // Stock every template in a template object so as to not fetch again if not needed
      if (Object.prototype.hasOwnProperty.call(this.templatesConfig, this.activeTemplate.name)) {
        const currentTemplate = this.templatesConfig[this.activeTemplate.name]
        this.specificOriginModel = currentTemplate.specificOriginModel
        this.specificTranslationsModel = currentTemplate.specificTranslationsModel
        this.localesAllowed = currentTemplate.localesAllowed
      } else {
        let baseUrl = this.activeTemplate.url.endsWith('/index.html') ? this.activeTemplate.url.replace('/index.html', '/') : this.activeTemplate.url
        if (!baseUrl.endsWith('/')) {
          baseUrl += '/'
        }
        const response = await window.fetch(`${baseUrl}config.json`, {
          method: 'GET',
          mode: 'cors',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'text/plain'
          }
        })

        const { specificOriginModel, specificTranslationsModel, localesAllowed } = await response.json()

        this.templatesConfig[this.activeTemplate.name] = {
          specificOriginModel,
          specificTranslationsModel,
          localesAllowed
        }

        this.specificOriginModel = specificOriginModel
        this.specificTranslationsModel = specificTranslationsModel
        this.localesAllowed = localesAllowed
        this.loading = false
      }
    },

    async getJobs () {
      const response = await this.service.get()
      this.jobs = response.filter(j => j.data.general !== undefined)
    },

    addEventListeners () {
      this.service.on('progress', this.progressUpdate)
      this.service.on('completed', this.completedUpdate)
      bus.$on('clear-failed', this.clearFailed)
    },

    removeEventListeners () {
      this.service.off('progress', this.progressUpdate)
      this.service.off('completed', this.completedUpdate)
      bus.$off('clear-failed', this.clearFailed)
    },

    progressUpdate ({ jobId, progress }) {
      const job = this.jobs.find(j => j.id === jobId)
      if (job !== undefined) {
        job.progress = parseFloat(progress)
      } else {
        console.error('No job found.')
      }
    },

    completedUpdate ({ jobId, result }) {
      const job = this.jobs.find(j => j.id === jobId)
      job.status = 'completed'
      job.returnvalue = result
    },

    async validate (data) {
      data.forEach(video => {
        video.data.user = {
          email: this.userEmail,
          name: this.userName
        }
        video.data.general = {
          ...video.data.general,
          template: this.selectedTemplateName
        }
      })
      const response = await this.service.create(data)
      if (response.length > 0) {
        this.rendering = true
        this.jobs.push(...response)
      }
    },

    async clearFailed () {
      this.service.remove('failed')
    }
  }
}
</script>

<style lang="scss">
  @import "~@/assets/styles/variables";
  @import "~@/assets/styles/fonts";
  @import "~@/assets/styles/icons";

  .template{
    font-family: $poppins;
    h1{ display: inline-block; }

    .wrapper {
      display: flex;

      .settingsForm, .side-bar { margin: 0; min-width: 300px; }

      h2 {
        position: relative;
        &:before{
          content: '';
          display: block;
          position: absolute;
          left: -48px;
          $triangle_width: 12px;
          top: 0;
          border-top: $triangle_width solid transparent;
          border-left: $triangle_width solid white;
          border-bottom: $triangle_width solid transparent;
        }
      }

      .preview {
        flex: 1;
      }

      .side-bar {
        background: #364040;
        color: white;
        padding: 48px;
        width: 600px;
        flex-shrink: 0;
      }
    }
  }
  header {
    position: relative;
    $border: 1px solid #a1b2b2;
    padding: 24px 0;
    margin-bottom: 24px;

    &.no-border {
      border: none;
      padding: 0;
      margin-bottom: 12px;
    }
    &.with-button {
      display: flex;
      justify-content: space-between;
      align-items: center;

      button {
        background: #a4b4b4;
        color: #364040;
      }
    }

    h2, h3 { margin-bottom: 0}
  }
</style>
